import React, { useEffect, useState } from "react";

import { getData, putData } from "../../Services/apiService";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../Provider/Alert/AlertContext";
import Loading from "../Layout/Components/Loading";
import Menu from "../../Helpers/menu";
import FixedTable from "../Layout/Components/Table/FixedTable";
import { FileEarmarkLock } from "react-bootstrap-icons";
import {
    PencilSquare,
    Ban,
    Check2Circle,
    BookmarkStar,
} from "react-bootstrap-icons";
import LicenseForm from "./Components/LicenseForm";
import {
    Modal,
    Button,
    Container,
    Row,
    Col,
    FloatingLabel,
    Form,
} from "react-bootstrap";
import { useContract } from "../../Hook/Contract/useContract";

export default function Licenses({ idClient }) {
    const { t } = useTranslation(); // Translation hook
    const { addAlert } = useAlert(); // Alert hook
    const [loading, setLoading] = useState(true); // Loading hook
    const [reload, setReload] = useState(0); // Reload hook
    const { contracts } = useContract({ idClient });

    // List the licenses
    const [licenses, setLicenses] = useState([]);

    const listLiceses = async () => {
        // Get the query string from the local storage
        let queryString = JSON.parse(localStorage.getItem("licenses"));
        if (!queryString) {
            queryString = {
                page: 1,
                limit: 10,
                totalItems: 0,
                order: [{ field: "id", direction: "asc" }],
            };
            localStorage.setItem("licenses", JSON.stringify(queryString));
        }

        // Get the licenses for the client
        let uri = `/client/${idClient}/license?page=${queryString.page}&limit=${queryString.limit}`;

        // Adicionar ordenações à URI
        const orderString = encodeURIComponent(
            JSON.stringify(queryString.order)
        );
        uri += `&order=${orderString}`;

        // Await the response
        try {
            const getLicenses = await getData(uri);

            // If the response is OK, set the licenses and the query string
            if (getLicenses.status === 200 || getLicenses.status === 404) {
                setLicenses(getLicenses.body.data || []);
                queryString.lastPage = getLicenses.body.total_pages;
                queryString.totalItems = getLicenses.body.total_items;
                localStorage.setItem("licenses", JSON.stringify(queryString));
                setLoading(false);
                return;
            }

            addAlert("danger", t("license.alert.error"), getLicenses.body);
        } catch (error) {
            addAlert("danger", t("license.alert.error"), error);
        }
    };

    // Edit license
    const [showEditLicense, setShowEditLicense] = useState(false);
    const [license, setLicense] = useState({});
    const editLicense = (data) => {
        setLicense(data);
        setShowEditLicense(true);
    };
    const handleCloseEditLicense = () => {
        setShowEditLicense(false);
        setLicense({});
    };

    // Column definitions
    const tableColumns = [
        { field: "id", label: "#", type: "number" },
        {
            field: "client.name",
            label: t("licenses.table.client"),
            type: "string",
        },
        {
            field: "contract.description",
            label: t("licenses.table.contract"),
            type: "string",
        },
        {
            field: "application.name",
            label: t("licenses.table.application"),
            type: "string",
        },
        {
            field: "number_lane",
            label: t("licenses.table.number_lane"),
            type: "number",
        },
        { field: "serial", label: t("licenses.table.serial"), type: "string" },
        {
            field: "status",
            label: t("licenses.table.status"),
            type: "string",
            style: {
                color: (row) => {
                    switch (row.status) {
                        case "NEW":
                            return "yellow";
                        case "ACTIVE":
                            return "green";
                        case "BLOCKED":
                            return "red";
                        case "INACTIVE":
                            return "grey";
                        default:
                            break;
                    }
                },
                fontWeight: "bold",
                textAlign: "center",
            },
            value: {
                NEW: t("licenses.status.new"),
                ACTIVE: t("licenses.status.active"),
                BLOCKED: t("licenses.status.blocked"),
                INACTIVE: t("licenses.status.inactive"),
            },
        },
        {
            field: "last_access",
            label: t("licenses.table.last_access"),
            type: "date-time",
        },
        {
            field: "expires_at",
            label: t("licenses.table.expires_at"),
            type: "date",
        },
        {
            field: "created_at",
            label: t("licenses.table.create_at"),
            type: "date-time",
        },
        {
            field: "update_user.name",
            label: t("licenses.table.update_user"),
            type: "string",
        },
        {
            field: "update_at",
            label: t("licenses.table.update_at"),
            type: "date-time",
        },
        {
            field: "actions",
            label: t("licenses.table.actions"),
            type: "actions",
            actions: [{ icon: <PencilSquare />, handle: editLicense }],
        },
    ];

    // Menu definitions
    const tableMenu = [
        {
            label: t("licenses.table.menu.edit"),
            icon: <BookmarkStar />,
            handle: editLicense,
        },
        {
            label: t("licenses.table.menu.active"),
            icon: <Check2Circle />,
            handle: (data) => {
                handleActive(data);
            },
            disabled: (data) => {
                return data.status === "ACTIVE" ? true : false;
            },
        },
        {
            label: t("licenses.table.menu.block"),
            icon: <Ban />,
            handle: (data) => {
                handleBlock(data);
            },
        },
    ];

    // Active license
    const [showSelectContract, setShowSelectContract] = useState(false);
    const [licenceActivate, setLicenceActivate] = useState({});
    const handleActive = (licence) => {
        setLicenceActivate({
            ...licenceActivate,
            id_license: licence.id,
            serial: licence.serial,
            id_contract: licence.contract.id || contracts[0].id,
        });
        setShowSelectContract(true);
    };
    const handleSelectContractClose = () => {
        setShowSelectContract(false);
    };
    const handleSendActivation = async () => {
        try {
            const activeLicense = await putData(
                `/license/${licenceActivate.id_license}/active`,
                null,
                licenceActivate
            );
            if (activeLicense.status === 200) {
                addAlert(
                    "success",
                    t("license.alert.success"),
                    t("license.alert.active")
                );
                setShowSelectContract(false);
                setReload(reload + 1);
            } else {
                handleSelectContractClose();
                addAlert(
                    "danger",
                    t("license.alert.error"),
                    activeLicense.body
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleBlock = async (license) => {
        if (
            license.id === undefined ||
            license.id === null ||
            license.id === 0
        ) {
            return;
        }

        try {
            const blockLicense = await putData(
                `/license/${license.id}/block`,
                null,
                licenceActivate
            );

            if (blockLicense.status === 200) {
                addAlert(
                    "success",
                    t("license.alert.success"),
                    t("license.alert.active")
                );
                setShowSelectContract(false);
                setReload(reload + 1);
            } else {
                handleSelectContractClose();
                addAlert("danger", t("license.alert.error"), blockLicense.body);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const list = async () => {
            await listLiceses();
        };
        list();
        // eslint-disable-next-line
    }, [reload]);

    return (
        <>
            {loading ? (
                <Loading />
            ) : (
                <>
                    <LicenseForm
                        show={showEditLicense}
                        handleClose={handleCloseEditLicense}
                        license={license}
                        setReload={() => setReload(reload + 1)}
                    />
                    <Menu
                        icon={<FileEarmarkLock />}
                        placeholderSearch="licenses.menu.search"
                    />
                    <FixedTable
                        table="licenses"
                        columns={tableColumns}
                        data={licenses}
                        menu={tableMenu}
                        reload={() => setReload(reload + 1)}
                        empty={t("licenses.table.empty")}
                    />

                    <Modal
                        show={showSelectContract}
                        size="lg"
                        onHide={handleSelectContractClose}
                        backdrop="static"
                        keyboard={false}
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {t("license.contract.title")}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Container>
                                <Row>
                                    <Col>
                                        <FloatingLabel
                                            controlId="LicenseSelectContract"
                                            label={t(
                                                "licenses.contract.select"
                                            )}
                                        >
                                            <Form.Select
                                                aria-label="LicenseSelectContract"
                                                value={
                                                    licenceActivate.id_contract
                                                }
                                                onChange={(e) =>
                                                    setLicenceActivate({
                                                        ...licenceActivate,
                                                        id_contract: parseInt(
                                                            e.target.value,
                                                            10
                                                        ),
                                                    })
                                                }
                                            >
                                                {contracts &&
                                                contracts.length > 0 ? (
                                                    contracts.map(
                                                        (contract) =>
                                                            contract.is_active && (
                                                                <option
                                                                    key={
                                                                        contract.id
                                                                    }
                                                                    value={parseInt(
                                                                        contract.id,
                                                                        10
                                                                    )}
                                                                >
                                                                    {
                                                                        contract.description
                                                                    }
                                                                </option>
                                                            )
                                                    )
                                                ) : (
                                                    <option value="">
                                                        {t(
                                                            "licenses.contract.no_contracts"
                                                        )}
                                                    </option>
                                                )}
                                            </Form.Select>
                                        </FloatingLabel>
                                    </Col>
                                </Row>
                            </Container>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="danger"
                                onClick={handleSelectContractClose}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSendActivation}
                            >
                                Confirmar
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
        </>
    );
}
